import { useQuery } from '@tanstack/react-query';
import { Receipt } from 'lucide-react';
import { api } from '@/lib/api';
import { fmt } from '@/lib/format';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { cn } from '@/lib/cn';

type Row = {
  id: string;
  proveedor: string;
  folio?: string;
  fecha: string;
  fecha_vencimiento?: string;
  concepto?: string;
  importe: number;
  saldo: number;
  estado: 'pendiente' | 'parcial' | 'pagado' | 'cancelado';
};

export function CxPPage() {
  const { data, isLoading } = useQuery({
    queryKey: ['cxp'],
    queryFn: () => api<Row[]>('/cxp'),
  });

  const totalPendiente = (data ?? []).filter(r => r.estado !== 'pagado' && r.estado !== 'cancelado').reduce((a, r) => a + r.saldo, 0);

  return (
    <div className="flex flex-col gap-6">
      <div>
        <p className="text-[12px] font-semibold uppercase tracking-[0.14em] text-ink-mute">Tesorería · Cuentas por Pagar</p>
        <h1 className="mt-1 font-display text-[28px] font-bold tracking-tight text-ink">Proveedores pendientes de pago</h1>
        <p className="mt-1 text-[14px] text-ink-mute">
          Las CxP se generan automáticamente al devengar una recepción de almacén, orden de compra o estimación de obra.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Stat label="Saldo total pendiente" value={fmt.money(totalPendiente)} />
        <Stat label="Documentos activos"    value={(data ?? []).filter(r => r.estado !== 'pagado').length.toString()} />
        <Stat label="Vencidos"              value={(data ?? []).filter(r => r.fecha_vencimiento && new Date(r.fecha_vencimiento) < new Date() && r.estado !== 'pagado').length.toString()} />
      </div>

      <Card>
        <CardHeader title="Cuentas por pagar" />
        <CardBody className="p-0">
          {isLoading ? (
            <div className="p-10 text-center text-[13px] text-ink-mute">Cargando…</div>
          ) : !data || data.length === 0 ? (
            <div className="flex flex-col items-center gap-3 p-10 text-center">
              <Receipt className="h-7 w-7 text-ink-soft" />
              <p className="text-[13px] text-ink-mute">Sin CxP registradas. Aparecerán al devengar recepciones.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-[13px]">
                <thead className="bg-muted/60 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-mute">
                  <tr>
                    <th className="px-5 py-3">Folio</th>
                    <th className="px-5 py-3">Proveedor</th>
                    <th className="px-5 py-3">Fecha</th>
                    <th className="px-5 py-3">Vence</th>
                    <th className="px-5 py-3 text-right">Importe</th>
                    <th className="px-5 py-3 text-right">Saldo</th>
                    <th className="px-5 py-3">Estado</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {data.map((r) => (
                    <tr key={r.id} className="transition-colors hover:bg-muted/50">
                      <td className="num px-5 py-3 font-semibold">{r.folio ?? '—'}</td>
                      <td className="px-5 py-3">{r.proveedor}</td>
                      <td className="num px-5 py-3 text-ink-mute">{fmt.date(r.fecha)}</td>
                      <td className="num px-5 py-3 text-ink-mute">{r.fecha_vencimiento ? fmt.date(r.fecha_vencimiento) : '—'}</td>
                      <td className="num px-5 py-3 text-right">{fmt.money(r.importe)}</td>
                      <td className="num px-5 py-3 text-right font-semibold">{fmt.money(r.saldo)}</td>
                      <td className="px-5 py-3"><EstadoPill s={r.estado} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-border bg-surface p-5 shadow-card">
      <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-mute">{label}</span>
      <div className="num mt-2 text-[22px] font-semibold text-ink">{value}</div>
    </div>
  );
}

function EstadoPill({ s }: { s: Row['estado'] }) {
  const c: Record<Row['estado'], string> = {
    pendiente: 'bg-amber-50   text-amber-800    dark:bg-amber-900/40   dark:text-amber-200',
    parcial:   'bg-sky-50     text-sky-800      dark:bg-sky-900/40     dark:text-sky-200',
    pagado:    'bg-emerald-50 text-emerald-700  dark:bg-emerald-900/40 dark:text-emerald-200',
    cancelado: 'bg-stone-100  text-ink-mute     dark:bg-white/5',
  };
  return <span className={cn('rounded-full px-2 py-0.5 text-[11px] font-medium', c[s])}>{s}</span>;
}
